import { useMemo, useState } from 'react';
import type { CustodyCase, VehicleCategory } from '../types/case';
import type { DemoUser, FieldRole } from '../types/config';
import { useStore } from '../contexts/StoreContext';
import { DIVISION_STATION, MAP_PLACES } from '../data/config';
import { CATEGORY_PHOTO } from '../data/media';
import { buildDocuments } from '../utils/documents';
import { addDays, nowIso } from '../utils/bn';

export interface CustodyForm {
  category: VehicleCategory;
  regNo: string;
  plateIssue: boolean;
  brand: string;
  model: string;
  color: string;
  chassis: string;
  engine: string;
  photo: boolean;
  driverName: string;
  ownerName: string;
  mobile: string;
  reason: string;
  sergeant: string;
  division: string;
  road: string;
  x: number;
  y: number;
  placeLabel: string;
  description: string;
  scenePhoto: boolean;
  remarks: string;
}

const EMPTY: CustodyForm = {
  category: 'car', regNo: '', plateIssue: false, brand: '', model: '', color: '', chassis: '', engine: '', photo: false,
  driverName: '', ownerName: '', mobile: '', reason: '', sergeant: 'মোঃ আরিফ হোসেন', division: 'ট্রাফিক-তেজগাঁও বিভাগ',
  road: '', x: 0, y: 0, placeLabel: '', description: '', scenePhoto: false, remarks: '',
};

const SAMPLE: CustodyForm = {
  ...EMPTY,
  category: 'car', regNo: 'ঢাকা মেট্রো-ঘ-১৯-২২৮৪', brand: 'টয়োটা', model: 'এলিয়ন', color: 'রুপালি', chassis: 'NZT260-3108842', engine: '2NR-F0193377', photo: true,
  driverName: 'মোঃ সোহেল রানা', ownerName: 'মোঃ মাহমুদ রহমান', mobile: '01712345678',
  reason: 'ফিটনেস সনদ না থাকা / মেয়াদোত্তীর্ণ', road: 'ফার্মগেট মোড়, কাজী নজরুল ইসলাম এভিনিউ', x: 186, y: 254, placeLabel: 'ফার্মগেট',
  description: 'চেকপোস্টে কাগজপত্র যাচাইকালে ফিটনেস সনদের মেয়াদ উত্তীর্ণ পাওয়া যায়।', scenePhoto: true,
};

export function useCustodyForm(role: FieldRole, user: DemoUser) {
  const { config, createCase, nextPcr } = useStore();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<CustodyForm>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const set = <K extends keyof CustodyForm>(k: K, v: CustodyForm[K]) => {
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: '' }));
  };

  const pickPlace = (label: string) => {
    const p = MAP_PLACES.find((m) => m.label === label);
    if (p) setForm((f) => ({ ...f, x: p.x, y: p.y, placeLabel: p.label, road: f.road || p.road, division: p.division }));
  };

  const fee = useMemo(() => {
    const fine = config.categories.find((c) => c.key === form.category)?.fine ?? 0;
    const extra = config.reasons.find((r) => r.label === form.reason)?.extraCharge ?? 0;
    const service = config.serviceFee.enabled ? config.serviceFee.amount : 0;
    return { fine, extra, service, total: fine + extra };
  }, [config, form.category, form.reason]);

  const deadline = addDays(nowIso(), config.deadlineDays);
  const station = DIVISION_STATION[form.division] ?? 'তেজগাঁও ডাম্পিং স্টেশন';

  const validate = (s: number): boolean => {
    const e: Record<string, string> = {};
    if (s === 0) {
      if (!form.plateIssue && !form.regNo.trim()) e.regNo = 'রেজিস্ট্রেশন নম্বর দিন অথবা “নম্বরপ্লেট নেই / সমস্যাযুক্ত” নির্বাচন করুন।';
      if (form.plateIssue && !form.chassis.trim() && !form.engine.trim()) e.chassis = 'নম্বরপ্লেট না থাকলে চেসিস বা ইঞ্জিন নম্বর আবশ্যক।';
      if (!form.brand.trim()) e.brand = 'গাড়ির ব্র্যান্ড লিখুন।';
      if (!form.driverName.trim() && !form.ownerName.trim()) e.driverName = 'চালক অথবা মালিকের নাম আবশ্যক।';
      if (form.mobile && !/^01[3-9]\d{8}$/.test(form.mobile)) e.mobile = 'সঠিক ১১ সংখ্যার মোবাইল নম্বর দিন (০১XXXXXXXXX)।';
    }
    if (s === 1) {
      if (!form.reason) e.reason = 'আটকের কারণ নির্বাচন করুন।';
      if (!form.road.trim()) e.road = 'রাস্তার নাম লিখুন।';
      if (!form.x) e.map = 'মানচিত্রে অবস্থান নির্বাচন করুন অথবা জিপিএস লোকেশন নিন।';
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => { if (validate(step)) setStep((s) => Math.min(2, s + 1)); };
  const back = () => setStep((s) => Math.max(0, s - 1));

  const submit = (): CustodyCase | null => {
    if (!validate(0) || !validate(1)) return null;
    const isWrecker = role === 'wrecker';
    return createCase(
      {
        category: form.category,
        regNo: form.plateIssue && !form.regNo.trim() ? 'নম্বরপ্লেট নেই (অস্থায়ী শনাক্তকরণ)' : form.regNo.trim(),
        plateIssue: form.plateIssue,
        brand: form.brand, model: form.model, color: form.color, chassis: form.chassis, engine: form.engine,
        driverName: form.driverName, ownerName: form.ownerName || 'অজ্ঞাত', ownerAddress: '', ownerNid: '', mobile: form.mobile,
        reason: form.reason, division: form.division, road: form.road,
        location: { x: form.x, y: form.y, label: form.placeLabel || 'নির্বাচিত স্থান' },
        description: [form.description, form.remarks].filter(Boolean).join(' — '),
        sergeant: form.sergeant, wreckerId: 'WRK-07', operator: 'মোঃ সুমন মিয়া',
        fine: fee.fine, extraCharge: fee.extra, deadline, assignedStation: station, stage: 'registered',
        barcode: { attached: false, reprints: [] }, docStatus: 'none', documents: buildDocuments(form.category, config.docRules),
        paymentStatus: 'unpaid', payment: {}, approval: 'none', ownerLinked: !!form.mobile, photo: CATEGORY_PHOTO[form.category],
      },
      { actor: user.name, role: user.title, action: isWrecker ? 'PCR তৈরি ও জরিমানা নির্ধারণ' : 'হেফাজত নির্দেশনা ও রেকার অনুরোধ — WRK-07' },
    );
  };

  return { step, setStep, form, set, errors, pickPlace, fee, deadline, station, next, back, submit, fillSample: () => setForm(SAMPLE), pcrPreview: nextPcr() };
}
